import { objectService } from "@/api/services/object.service";
import * as Sentry from "@sentry/tanstackstart-react";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

const uploadMediaSchema = z.object({
  fileName: z.string().min(1, "File name is required"),
  fileSize: z.number().positive("File size must be positive"),
  contentType: z.string().min(1, "Content type is required"),
});

export const uploadMediaFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(uploadMediaSchema)
  .handler(async ({ context, data }) => {
    return await Sentry.startSpan(
      { name: "action.uploadMediaFn", op: "function" },
      async () => {
        if (context.auth.role !== "admin" && context.auth.role !== "author") {
          return {
            status: 403,
            body: { message: "Forbidden" },
          };
        }

        // Validate file type
        const ALLOWED_TYPES = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/gif",
          "image/webp",
        ];
        if (!ALLOWED_TYPES.includes(data.contentType)) {
          return {
            status: 400,
            body: { message: "Invalid file type. Only images are allowed." },
          };
        }

        // Validate file size (10MB max)
        const MAX_SIZE = 10 * 1024 * 1024;
        if (data.fileSize > MAX_SIZE) {
          return {
            status: 400,
            body: { message: "File too large. Maximum size is 10MB." },
          };
        }

        // Generate key and signed URL
        const extension = data.fileName.split(".").pop() || "jpg";
        const { id, key } = objectService.generateKey(extension);

        const signedUrl = await Sentry.startSpan(
          { name: "objectService.createObjectUrl", op: "db" },
          () =>
            objectService.createObjectUrl(key, data.fileSize, data.contentType),
        );

        return {
          status: 200,
          body: {
            id,
            key,
            signedUrl,
            fileName: data.fileName,
          },
        };
      },
    );
  });

const deleteMediaSchema = z.object({
  key: z.string().min(1, "Key is required"),
});

export const deleteMediaFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(deleteMediaSchema)
  .handler(async ({ context, data }) => {
    return await Sentry.startSpan(
      { name: "action.deleteMediaFn", op: "function" },
      async () => {
        if (context.auth.role !== "admin") {
          return {
            status: 403,
            body: { message: "Forbidden" },
          };
        }

        try {
          await Sentry.startSpan(
            { name: "objectService.deleteObject", op: "db" },
            () => objectService.deleteObject(data.key),
          );

          return {
            status: 200,
            body: { message: "File deleted successfully", key: data.key },
          };
        } catch (error) {
          return {
            status: 500,
            body: {
              message: error instanceof Error ? error.message : "Delete failed",
            },
          };
        }
      },
    );
  });

export const cleanGhostObjectsFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    return await Sentry.startSpan(
      { name: "action.cleanGhostObjectsFn", op: "function" },
      async () => {
        if (context.auth.role !== "admin") {
          throw new Error("Forbidden");
        }

        const result = await Sentry.startSpan(
          { name: "objectService.cleanGhostObjects", op: "db" },
          () => objectService.cleanGhostObjects(),
        );

        return {
          status: 200,
          body: result,
        };
      },
    );
  });

export const deleteAllOrphansFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    return await Sentry.startSpan(
      { name: "action.deleteAllOrphansFn", op: "function" },
      async () => {
        if (context.auth.role !== "admin") {
          throw new Error("Forbidden");
        }

        const result = await Sentry.startSpan(
          { name: "objectService.deleteAllOrphanObjects", op: "db" },
          () => objectService.deleteAllOrphanObjects(),
        );

        return {
          status: 200,
          body: result,
        };
      },
    );
  });
